import type { ReadonlyDeep } from '@amazon/vinyl-util'
import type { ResultOf } from 'gql.tada'

export type SanitizedGql<T> = ReadonlyDeep<
    T extends (infer U)[]
        ? NonNullable<SanitizedGql<U>>[]
        : T extends object
          ? {
                [K in keyof T]-?: K extends 'node' | 'edges'
                    ? NonNullable<SanitizedGql<T[K]>>
                    : SanitizedGql<T[K]>
            }
          : T
>

/** Sanitized result type of a gql document — the shape a sanitized fetch resolves to. */
export type SanitizedResult<Q> = SanitizedGql<ResultOf<Q>>

/**
 * In a GQL response, if `node` or `edges` is null, consider the parent entity invalid and exchange
 * for null. This simplifies type safety by eliminating null paths for node and edges.
 * @param value
 */
export function sanitizeGql<T>(value: T): SanitizedGql<T>

export function sanitizeGql(value: any): any {
    if (value == null) return value
    if (Array.isArray(value))
        return value.map(sanitizeGql).filter((element: any) => element != null)
    if (typeof value === 'object') {
        const obj: any = {}
        for (const key in value) {
            if (key === 'node' || key === 'edges') {
                // There is a null value for node or edges, parent object
                // is invalid; return null.
                if (value[key] == null) return null
            }
            obj[key] = sanitizeGql(value[key])
        }
        return obj
    }
    return value
}
